import * as apiService from '@/../vue/online-booking/utils/api';
// import * as stubs from '@/../vue/online-booking/utils/stubs';
import { apiDateString, withinRange } from '@/../vue/online-booking/utils/dates';
import { DateTime } from 'luxon';

// Model-ish setup for persisting API booking data (location, appointment etc) and handling errors
// Required loadWhile to be available from alerts mixin

export default {
    data: () => ({
        locations: [],
        locationId: undefined, // Chosen location
        appointments: [],
        appointmentId: null, // Final chosen appointment
        practitionerId: null,
        dateRangeStart: null, // Today
        dateRangeEnd: null,
        weekday: null,
        practitioners: [],
        serviceType: 'Optical',
        bookingHolded: null,
        appointmentStatusReady: false, // Signals that detals have been validated
        newPatient: null
    }),

    computed: {
        selectedLocation() {
            return this.locations.find((loc) => loc.locationId === this.locationId);
        },
        selectedAppointment() {
            return this.appointments.find((apt) => apt.appointmentId === this.appointmentId);
        },
        selectedPractitioner() {
            return (
                this.selectedAppointment &&
                this.practitioners.find(
                    (pract) => pract.employeeId === this.selectedAppointment.practitionerId
                )
            );
        },
        defaultDateRangeApplied() {
            return (
                this.dateRangeStart === apiDateString(this.southAusDateTime) &&
                this.dateRangeEnd === apiDateString(this.southAusDateTime, '6 months from now')
            );
        }
    },

    created() {
        this.setDefaultDateRange();
        // TESTING uncomment to stub data for quick testing
        // this.uid = 'hpb-6270ee241c2124.65843010';
        // this.locationId = 8;
        // this.serviceType = 'dental';
        // this.locations = stubs.locations;
        // this.appointments = stubs.appointments;
        // this.practitioners = stubs.practitioners;
        // this.appointmentId = 32212671;
    },

    methods: {
        async createBooking(serviceType) {
            const { data } = await this.loadWhile(apiService.createBooking(serviceType));
            this.locations = data.data.locations;
            this.uid = data.data.record.uid;
            return data;
        },

        async expire() {
            // Can be used to force expire the session for testing
            await this.loadWhile(apiService.expire(this));
        },

        async updateBooking() {
            const { data } = await this.loadWhile(apiService.updateBooking(this), true);
            this.practitioners = data.data.practitioners || this.practitioners; //to do : API should always return an array
            this.appointments = data.data.appointments || [];
            this.isLoading = false;
            return data;
        },

        async holdBooking() {
            const response = await this.loadWhile(apiService.holdBooking(this));
            this.bookingHolded = Date.now();
            return response;
        },

        async releaseBooking() {
            const response = await this.loadWhile(apiService.releaseBooking(this));
            this.bookingHolded = null;
            this.appointmentStatusReady = false;
            return response;
        },

        async completeBooking() {
            const response = await this.loadWhile(apiService.completeBooking(this));
            return response;
        },

        async getBooking() {
            const response = await this.loadWhile(apiService.getBooking(this));
            return response;
        },

        async validateDetails() {
            if (this.appointmentStatusReady) {
                await this.loadWhile(apiService.removePatient(this));
            }
            const response = await this.loadWhile(apiService.validateDetails(this));
            this.appointmentStatusReady = true;
            return response;
        },

        updatePractitioner(practitionerId) {
            if (practitionerId === this.practitionerId) return;
            this.practitionerId = practitionerId;
            this.updateBooking();
        },

        updateWeekday(day) {
            this.weekday = day;
            this.updateBooking();
        },

        updateDateRange(rangeStart) {
            if (this.defaultDateRangeApplied && !rangeStart) return;
            if (!rangeStart) {
                this.setDefaultDateRange();
            } else {
                rangeStart = DateTime.fromFormat(rangeStart, 'd/M/yy').toFormat('yyyy-MM-dd');
                if (withinRange(rangeStart, this.dateRangeStart, this.dateRangeEnd)) return;
                this.dateRangeStart = rangeStart;
                this.dateRangeEnd = rangeStart;
            }
            this.updateBooking();
        },

        setDefaultDateRange() {
            this.dateRangeStart = apiDateString(this.southAusDateTime); // Today
            this.dateRangeEnd = apiDateString(this.southAusDateTime, '6 months from now');
        }
    }
};
